import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import { toast } from 'sonner';
import { Presentation, Loader2, Download, FileJson, Sparkles } from 'lucide-react';
import { useCreditGate } from '../CreditGate';

const MeetingPrepPanel = () => {
  const { getAuthHeaders } = useAuth();
  const { requestRun, dialog } = useCreditGate('meeting_prep', 'Confirm Meeting Prep Brief');
  const [vendorName, setVendorName] = useState('');
  const [docIds, setDocIds] = useState('');
  const [meetingType, setMeetingType] = useState('vendor_review');
  const [includeRisk, setIncludeRisk] = useState(true);
  const [outputFormat, setOutputFormat] = useState('json');
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [recent, setRecent] = useState([]);

  const loadRecent = async () => {
    try {
      const res = await api.getRecentBriefs(getAuthHeaders());
      setRecent(res.data?.briefs || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    api.getKnownVendors(getAuthHeaders()).then((r) => setVendors(r.data?.vendors || [])).catch(() => {});
    loadRecent();
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    if (!vendorName && !docIds) {
      toast.error('Provide a vendor name or document IDs');
      return;
    }
    setLoading(true);
    setBrief(null);
    try {
      const fd = new FormData();
      if (vendorName) fd.append('vendor_name', vendorName);
      if (docIds) fd.append('doc_ids', docIds);
      fd.append('meeting_type', meetingType);
      fd.append('include_risk', includeRisk ? 'true' : 'false');
      fd.append('output_format', outputFormat);

      if (outputFormat === 'json') {
        const res = await api.prepareMeetingBriefJSON(fd, getAuthHeaders());
        setBrief(res.data?.brief || null);
        toast.success('Brief generated');
      } else {
        const res = await api.prepareMeetingBrief(fd, getAuthHeaders());
        const mime = outputFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `meeting_brief.${outputFormat}`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`${outputFormat.toUpperCase()} downloaded`);
      }
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate brief');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="meeting-prep-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Presentation className="w-5 h-5 text-indigo-600" /> Meeting Preparation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Vendor name</Label>
            <Input
              data-testid="mp-vendor-input"
              list="known-vendors"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder="e.g. ABC Services LLC"
              className="mt-1"
            />
            <datalist id="known-vendors">
              {vendors.map((v) => <option key={v} value={v} />)}
            </datalist>
          </div>
          <div className="text-center text-xs text-slate-400">— or —</div>
          <div>
            <Label className="text-xs">Document IDs (comma-separated)</Label>
            <Input
              data-testid="mp-docids-input"
              value={docIds}
              onChange={(e) => setDocIds(e.target.value)}
              placeholder="id1, id2, id3"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Meeting type</Label>
            <select
              data-testid="mp-type-select"
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value)}
              className="mt-1 h-9 w-full border rounded-md px-2 text-sm bg-white"
            >
              <option value="vendor_review">Vendor Review</option>
              <option value="board">Board Meeting</option>
              <option value="contract_review">Contract Review</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Include risk summary</Label>
            <Switch data-testid="mp-risk-toggle" checked={includeRisk} onCheckedChange={setIncludeRisk} />
          </div>
          <div>
            <Label className="text-xs">Output format</Label>
            <select
              data-testid="mp-format-select"
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              className="mt-1 h-9 w-full border rounded-md px-2 text-sm bg-white"
            >
              <option value="json">JSON preview</option>
              <option value="pdf">PDF download</option>
              <option value="docx">DOCX download</option>
            </select>
          </div>
          <Button data-testid="mp-generate-btn" onClick={() => requestRun(submit)} disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            Generate Brief
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileJson className="w-4 h-4" /> Brief preview</CardTitle></CardHeader>
          <CardContent>
            {!brief ? (
              <p className="text-sm text-slate-400 py-10 text-center" data-testid="mp-empty">Generate a brief to preview it here (JSON format)</p>
            ) : (
              <div className="space-y-3 text-sm" data-testid="mp-brief-preview">
                <p className="text-slate-700">{brief.executive_overview}</p>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Action items</p>
                  <ul className="list-disc list-inside text-slate-600 text-xs space-y-0.5">
                    {brief.action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
                {brief.risk_summary?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Risk summary</p>
                    {brief.risk_summary.map((r, i) => (
                      <div key={i} className="text-xs text-red-600">{r.title} — {r.risk_level} ({r.risk_score})</div>
                    ))}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Documents ({brief.document_count})</p>
                  <ul className="text-xs text-slate-500 space-y-0.5 max-h-32 overflow-y-auto">
                    {brief.document_index.map((d, i) => <li key={i}>{d.title} ({d.doc_type}, {d.risk_level})</li>)}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Download className="w-4 h-4" /> Recent briefs</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="mp-recent">
              {recent.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No briefs generated yet</p>
              ) : (
                recent.map((b) => (
                  <div key={b.id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-2">
                    <div>
                      <span className="font-medium text-slate-700">{b.vendor_name || b.meeting_type}</span>
                      <span className="text-slate-400"> · {b.document_count} docs · {b.output_format}</span>
                    </div>
                    <span className="text-slate-400">{(b.created_at || '').slice(0, 10)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      {dialog}
    </div>
  );
};

export default MeetingPrepPanel;
